'use client';

import { useEffect, useState } from 'react';
import BurgerMenu from './BurgerMenu';
import GlobalSearch from './GlobalSearch';

import { usePathname } from 'next/navigation';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { FaStar, FaRegStar } from 'react-icons/fa6';
import { SessionType } from '@/util/types/globalTypes';
import { callServerAction } from '@/util/functions/fetching/callServerAction';

import { UserSettings } from '@/util/types/serverTypes';
import { Image, Tooltip, addToast, Button } from '@heroui/react';
import { useClientMounted } from '@/util/hooks/useClientMounted';

export default function InPageNav({
  // title,
  // links,
  session,
  userSettings,
}: {
  // title: string;
  // links: { to: string; label: string }[];
  session: SessionType;
  userSettings: UserSettings | null;
}) {
  const pathname = usePathname();
  const mounted = useClientMounted();

  const [pageTitle, setPageTitle] = useState('');
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const isFav = !!userSettings?.favorites?.find((fav) => fav.slug === pathname);

  useEffect(() => {
    setPageTitle(document.title);
  }, [pathname]);

  const handleFavSelect = async () => {
    // console.log('fav clicked');
    try {
      const body = {
        slug: pathname,
        title: pageTitle,
        _userId: session.user._id,
      };

      if (!isFav) {
        await callServerAction(
          {
            url: `user-settings/favorites`,
            method: 'post',
            data: body,
          },
          undefined,
          pathname
        );

        addToast({
          title: 'Favorite Page Added',
          description: 'Your favorites have been updated.',
        });
      }

      if (isFav) {
        const foundFav = userSettings?.favorites?.find(
          (fav) => fav.slug === pathname
        );
        await callServerAction(
          {
            url: `user-settings/favorites/${foundFav?._id}`,
            method: 'delete',
            data: body,
          },
          undefined,
          pathname
        );

        addToast({
          title: 'Favorite Page Removed',
          description: 'Your favorites have been updated.',
        });
      }

      // router.refresh();
    } catch (error) {
      console.error(error);
      addToast({
        title: 'Error',
        description: 'There was an error updating your favorites.',
        color: 'danger',
      });
    }
  };

  return (
    <div
      className={`min-h-16 fixed z-40 flex w-full items-center justify-between gap-2 p-4 backdrop-blur-md md:justify-start md:gap-0`}
    >
      {/* Left side - Logo, Title, and Favorites (hidden on mobile when search is open) */}
      <div
        className={`flex items-center gap-2 ${
          isMobileSearchOpen ? 'hidden md:flex' : 'flex'
        }`}
      >
        {session.user.memberships?.length > 0 && session.user.company?.logo && (
          <Image
            src={session.user.company.logo}
            alt={session.user.company.name}
            width={50}
            radius='none'
            className='max-h-10 object-contain'
          />
        )}
        {session.user.memberships?.length > 0 && session.user.company && !session.user.company.logo && (
          <div className='flex h-7 w-7 items-center justify-center rounded-full bg-white text-sm font-bold'>
            {(session.user.company.name ?? '').slice(0, 3).toUpperCase()}
          </div>
        )}
        <h3 className='truncate text-nowrap font-mono !text-base font-bold uppercase text-main'>
          {pageTitle || 'Attik'}
        </h3>
        {mounted ? (
          <Tooltip
            delay={400}
            content={isFav ? 'Remove From Favorites' : 'Add Page To Favorites'}
            placement='bottom'
            color='foreground'
            closeDelay={0}
            showArrow
          >
            <button
              className={`relative outline-none hover:text-themeYellow ${
                isFav ? 'text-themeYellow' : 'text-gray-600'
              }`}
              onClick={handleFavSelect}
            >
              {isFav ? (
                <FaStar className='-mt-1 inline' size={20} />
              ) : (
                <FaRegStar className='-mt-1 inline' size={20} />
              )}
            </button>
          </Tooltip>
        ) : (
          <button
            className={`relative outline-none hover:text-themeYellow ${
              isFav ? 'text-themeYellow' : 'text-gray-600'
            }`}
            onClick={handleFavSelect}
            aria-label={isFav ? 'Remove from favorites' : 'Add page to favorites'}
          >
            {isFav ? (
              <FaStar className='-mt-1 inline' size={20} />
            ) : (
              <FaRegStar className='-mt-1 inline' size={20} />
            )}
          </button>
        )}
      </div>
      <div className='flex items-center gap-2'>
        {/* Mobile Search Button (visible on mobile when search is closed) */}
        {mounted ? (
          <Tooltip
            content='Search Everything'
            placement='bottom'
            color='foreground'
            closeDelay={50}
            showArrow
          >
            <Button
              isIconOnly
              variant='light'
              className={`md:hidden ${isMobileSearchOpen ? 'hidden' : ''}`}
              onPress={() => setIsMobileSearchOpen(true)}
              aria-label='Open search'
            >
              <FaSearch size={19} />
            </Button>
          </Tooltip>
        ) : (
          <Button
            isIconOnly
            variant='light'
            className={`md:hidden ${isMobileSearchOpen ? 'hidden' : ''}`}
            onPress={() => setIsMobileSearchOpen(true)}
            aria-label='Open search'
          >
            <FaSearch size={19} />
          </Button>
        )}
        {/* Mobile Search Close Button (visible on mobile when search is open) */}

        {/* Burger Menu (hidden on mobile when search is open) */}
        <div className={`${isMobileSearchOpen ? 'hidden md:block' : 'block'}`}>
          <BurgerMenu />
        </div>
      </div>

      {/* Search Bar */}
      <div
        className={` flex w-full max-w-[min(100vw-1rem,28rem)] md:max-w-md lg:max-w-2xl xl:max-w-3xl ${
          isMobileSearchOpen
            ? 'grow justify-center gap-2' // Always show when mobile search is open
            : '!absolute left-1/2 ml-[-38px] hidden -translate-x-1/2 justify-center md:block' // Hidden on mobile, visible on desktop
        }`}
      >
        <Button
          isIconOnly
          variant='light'
          className={`md:hidden ${isMobileSearchOpen ? '' : 'hidden'}`}
          onPress={() => setIsMobileSearchOpen(false)}
          aria-label='Close search'
        >
          <FaTimes size={16} />
        </Button>
        <GlobalSearch
          className='w-full max-w-[min(100vw-1rem,28rem)] md:max-w-md lg:max-w-2xl xl:max-w-3xl'
          session={session}
        />
      </div>
    </div>
  );
}
